// Simple math renderer that converts LaTeX to HTML to eliminate flickering

const mathSymbols = {
  '\\sin': 'sin',
  '\\cos': 'cos',
  '\\tan': 'tan',
  '\\pi': 'π',
  '\\theta': 'θ',
  '\\alpha': 'α',
  '\\beta': 'β',
  '\\gamma': 'γ',
  '\\delta': 'δ',
  '\\epsilon': 'ε',
  '\\lambda': 'λ',
  '\\mu': 'μ',
  '\\sigma': 'σ',
  '\\phi': 'φ',
  '\\psi': 'ψ',
  '\\omega': 'ω',
  '\\infty': '∞',
  '\\pm': '±',
  '\\mp': '∓',
  '\\times': '×',
  '\\div': '÷',
  '\\neq': '≠',
  '\\leq': '≤',
  '\\geq': '≥',
  '\\approx': '≈',
  '\\equiv': '≡',
  '\\sqrt': '√',
  '\\sum': '∑',
  '\\prod': '∏',
  '\\int': '∫',
  '\\partial': '∂',
  '\\nabla': '∇',
  '\\Delta': 'Δ',
  '\\Sigma': 'Σ',
  '\\Omega': 'Ω',
  '\\degree': '°',
  '°': '°',
  '\\cdot': '·',
  '\\cdots': '⋯',
  '\\ldots': '…',
  '\\vdots': '⋮',
  '\\ddots': '⋱',
  '\\triangle': '△',
  '\\angle': '∠',
  '\\perp': '⊥',
  '\\parallel': '∥',
  '\\cong': '≅',
  '\\sim': '∼',
  '\\propto': '∝',
  '\\subset': '⊂',
  '\\supset': '⊃',
  '\\subseteq': '⊆',
  '\\supseteq': '⊇',
  '\\in': '∈',
  '\\notin': '∉',
  '\\cup': '∪',
  '\\cap': '∩',
  '\\emptyset': '∅',
  '\\forall': '∀',
  '\\exists': '∃',
  '\\nexists': '∄',
  '\\therefore': '∴',
  '\\because': '∵',
  '\\implies': '⟹',
  '\\iff': '⟺',
  '\\rightarrow': '→',
  '\\leftarrow': '←',
  '\\leftrightarrow': '↔',
  '\\Rightarrow': '⇒',
  '\\Leftarrow': '⇐',
  '\\Leftrightarrow': '⇔'
};

const renderFraction = (numerator, denominator) => {
  return `<span class="fraction">
    <span class="numerator">${numerator}</span>
    <span class="denominator">${denominator}</span>
  </span>`;
};

const renderSuperscript = (base, exponent) => {
  return `${base}<sup>${exponent}</sup>`;
};

const renderSubscript = (base, subscript) => {
  return `${base}<sub>${subscript}</sub>`;
};

const renderSqrt = (content) => {
  return `<span class="sqrt">√<span class="sqrt-content">${content}</span></span>`;
};

const renderCases = (content) => {
  // Parse the cases content and format it as a system of equations
  const lines = content.split('\\\\').map(line => line.trim());
  const formattedLines = lines.map(line => `<div class="case-line">${line}</div>`).join('');
  
  return `<span class="cases">
    <span class="cases-left">{</span>
    <span class="cases-content">${formattedLines}</span>
  </span>`;
};

// Helper function to find matching brace
const findMatchingBrace = (text, startIndex) => {
  let braceCount = 0;
  for (let i = startIndex; i < text.length; i++) {
    if (text[i] === '{') {
      braceCount++;
    } else if (text[i] === '}') {
      braceCount--;
      if (braceCount === 0) {
        return i;
      }
    }
  }
  return -1; // No matching brace found
};

export const renderMathToHTML = (mathText) => {
  if (!mathText || typeof mathText !== 'string') {
    return mathText;
  }

  let result = mathText;

  // Remove LaTeX delimiters first
  result = result.replace(/^\$|\$$/g, ''); // Remove leading/trailing dollar signs
  result = result.replace(/\\\(|\\\)/g, '');
  result = result.replace(/\$|\$/g, '');
  result = result.replace(/\\\[|\\\]/g, '');

  // Handle cases environment: \begin{cases} ... \end{cases}
  result = result.replace(/\\begin\{cases\}(.*?)\\end\{cases\}/gs, (match, content) => {
    return renderCases(content);
  });

  // Handle fractions: \frac{a}{b} with proper brace matching
  let fracIndex;
  while ((fracIndex = result.indexOf('\\frac')) !== -1) {
    const afterFrac = result.substring(fracIndex + 5); // 5 is length of '\frac'
    
    // Find the opening brace after \frac
    const openBraceIndex = afterFrac.indexOf('{');
    if (openBraceIndex === -1) break;
    
    // Find the matching closing brace for numerator
    const numEndIndex = findMatchingBrace(afterFrac, openBraceIndex);
    if (numEndIndex === -1) break;
    
    // Find the opening brace for denominator
    const denStartIndex = afterFrac.indexOf('{', numEndIndex + 1);
    if (denStartIndex === -1) break;
    
    // Find the matching closing brace for denominator
    const denEndIndex = findMatchingBrace(afterFrac, denStartIndex);
    if (denEndIndex === -1) break;
    
    const numerator = afterFrac.substring(openBraceIndex + 1, numEndIndex);
    const denominator = afterFrac.substring(denStartIndex + 1, denEndIndex);
    
    // Calculate the full match length
    const fullMatchLength = 5 + denEndIndex + 1; // '\frac' + content + closing brace
    
    // Replace the fraction
    const replacement = renderFraction(numerator, denominator);
    result = result.substring(0, fracIndex) + replacement + result.substring(fracIndex + fullMatchLength);
  }

  // Handle square roots: \sqrt{content}
  result = result.replace(/\\sqrt\{([^}]+)\}/g, (match, content) => {
    return renderSqrt(content);
  });

  // Handle superscripts: x^{2} or x^2
  result = result.replace(/([a-zA-Z0-9\)]+)\^\{([^}]+)\}/g, (match, base, exp) => {
    return renderSuperscript(base, exp);
  });
  result = result.replace(/([a-zA-Z0-9\)]+)\^([a-zA-Z0-9])/g, (match, base, exp) => {
    return renderSuperscript(base, exp);
  });

  // Handle subscripts: x_{2} or x_2
  result = result.replace(/([a-zA-Z0-9\)]+)_\{([^}]+)\}/g, (match, base, sub) => {
    return renderSubscript(base, sub);
  });
  result = result.replace(/([a-zA-Z0-9\)]+)_([a-zA-Z0-9])/g, (match, base, sub) => {
    return renderSubscript(base, sub);
  });

  // Replace math symbols
  Object.entries(mathSymbols).forEach(([latex, html]) => {
    const regex = new RegExp(latex.replace(/\\/g, '\\\\'), 'g');
    result = result.replace(regex, html);
  });

  // Handle simple cases
  result = result.replace(/\{([^}]+)\}/g, '$1'); // Remove remaining braces
  result = result.replace(/\\\s/g, ' '); // Handle escaped spaces

  // Handle line breaks: \n to <br> (process this last to avoid interference)
  result = result.replace(/\\n/g, '<br>');

  return result;
};

export const MathRenderer = ({ children, className = '', style = {} }) => {
  const renderedHTML = renderMathToHTML(children);
  
  return (
    <span 
      className={`math-rendered ${className}`}
      style={style}
      dangerouslySetInnerHTML={{ __html: renderedHTML }}
    />
  );
}; 